import React, { useRef, useState, useEffect, useMemo } from 'react';
import { Provider } from 'react-redux';
import {
  HashRouter,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';
import { locale, language } from '@tcc/i18n';
import { attachToGlobal } from '@tcc/treadstone'
import { getIdsFromHash } from './utils/url';
import { reqAppInitializer } from './actions/appInitializerActions';
import { MULTI_OPERATION_SELECTION } from './constants/featureFlags';
import { setupAllSteps } from './boot/config';
import { getRouteComponents } from './routes';
import configureStore from './store';
import { experimentalRedirect } from './boot/libs';
import { receiveOperations, receiveMultipleOperations } from './actions/operationsActions';
import { DmbQueryClientProvider } from './providers/DmbQueryClientProvider';
import { startView } from './utils/datadog';
import App from './containers/App/App';
import './global.css';

const asElement = (Component) => (Component ? <Component /> : undefined);

function RoutedApp() {
  const params = useParams();
  const location = useLocation();
  const routeProps = useMemo(() => {
    // App reconciles Redux whenever router-derived ownProps change.
    const route = { params, location };
    const components = getRouteComponents(route.params.layers);
    return Object.fromEntries(
      Object.entries(components).map(([name, Component]) => [
        name,
        asElement(Component),
      ])
    );
  }, [params, location]);

  return <App {...routeProps} />;
}

function DmbPlugin({ operations, operationsSelected }) {
  const [bootstrapped, setBootstrapped] = useState(false);
  const store = useRef();
  const [isProxying, setIsProxying] = useState(false);

  useEffect(() => {
    startView('dmb');
  }, []);

  useEffect(() => {
    attachToGlobal();
    setupAllSteps().then(({ nav, env, auth }) => {
      const { currentUser, user } = auth;

      setIsProxying(user.isProxying);
      experimentalRedirect({ name: env.name, href: env.href });

      const storeConfig = {
        currentUser,
        user,
        app: {
          locale,
          language,
          ...env,
          nav: {
            ...nav,
          },
        },
      };
      store.current = configureStore(storeConfig);

      const { fieldId, mapLayerIds, seasonIds } = getIdsFromHash();

      store.current.dispatch(
        reqAppInitializer(fieldId, mapLayerIds, seasonIds)
      );
      setBootstrapped(true);
    });
  }, []);

  useEffect(() => {
    if (!bootstrapped || isProxying || !operations?.length || !store.current) {
      return;
    }

    const state = store.current.getState();
    if (state.feature.userConfigFeatures[MULTI_OPERATION_SELECTION]?.value) {
      store.current.dispatch(receiveMultipleOperations(operationsSelected, operations));
    } else {
      store.current.dispatch(receiveOperations(operationsSelected[0], operations));
    }
  }, [bootstrapped, isProxying, operations, operationsSelected]);

  if (!bootstrapped) return null;

  return (
    <DmbQueryClientProvider>
      <Provider store={store.current}>
        <HashRouter>
          <Routes>
            <Route path="/dmb/:fieldId/:seasonId/:layers" element={<RoutedApp />} />
            <Route path="/dmb/:fieldId/:seasonId" element={<RoutedApp />} />
            <Route path="/dmb/:fieldId" element={<RoutedApp />} />
            <Route path="*" element={<RoutedApp />} />
          </Routes>
        </HashRouter>
      </Provider>
    </DmbQueryClientProvider>
  );
}

export default DmbPlugin;
